import { DateTime } from 'luxon';

export type TimeOption = {
  value: string; // "HH:mm"
  label: string; // "10:00 AM" or "10:00"
};

const MINUTES_PER_DAY = 24 * 60;

/** Detect whether the browser/locale prefers 24h time. */
export const detectBrowser24h = (): boolean => {
  const formatted = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
  }).format(new Date(2000, 0, 1, 13));
  // If "13" appears, it's 24h; if "1" with AM/PM markers, it's 12h.
  return formatted.includes('13');
};

/** Generate time options at the given interval. */
export const getTimeOptions = (step = 15, is24Hour?: boolean): TimeOption[] => {
  const is24h = is24Hour ?? detectBrowser24h();
  const options: TimeOption[] = [];

  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const dt = DateTime.fromObject({ hour: h, minute: m });
    const label = is24h ? dt.toFormat('HH:mm') : dt.toFormat('h:mm a').toLowerCase();
    options.push({ value, label });
  }

  return options;
};

/** "HH:mm" → display string (e.g. "10:00 AM" or "10:00"). */
export const formatTimeDisplay = (value: string, is24Hour?: boolean): string => {
  if (!value) return '';
  const dt = DateTime.fromFormat(value, 'HH:mm');
  if (!dt.isValid) return '';
  const is24h = is24Hour ?? detectBrowser24h();
  return is24h ? dt.toFormat('HH:mm') : dt.toFormat('h:mm a').toLowerCase();
};

/** Find the nearest option to a given value (for scroll-to). */
export const getNearestTimeOption = (
  value: string,
  options: TimeOption[],
): TimeOption | undefined => {
  if (!value || options.length === 0) return undefined;

  // Exact match first
  const exact = options.find((o) => o.value === value);
  if (exact) return exact;

  // Parse value to minutes
  const [h, m] = value.split(':').map(Number);
  if (h === undefined || m === undefined || isNaN(h) || isNaN(m)) return undefined;
  const targetMinutes = h * 60 + m;

  // Find closest
  let closest = options[0];
  let minDiff = Infinity;
  for (const option of options) {
    const [oh, om] = option.value.split(':').map(Number);
    if (oh === undefined || om === undefined || isNaN(oh) || isNaN(om)) continue;
    const optionMinutes = oh * 60 + om;
    const diff = Math.abs(optionMinutes - targetMinutes);
    if (diff < minDiff) {
      minDiff = diff;
      closest = option;
    }
  }
  return closest;
};

/** DOM id for a time option (used for scroll targeting). */
export const getTimeOptionId = (value: string): string => `time-option-${value}`;

export const parseTimeValue = (value: string): { hour: number; minute: number } | undefined => {
  if (!value) return undefined;

  const [hourPart, minutePart] = value.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (
    hourPart === undefined ||
    minutePart === undefined ||
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return undefined;
  }

  return { hour, minute };
};

export const adjustTimeByMinuteStep = (
  value: string,
  step = 15,
  direction: 'up' | 'down',
): string | undefined => {
  const parsed = parseTimeValue(value);
  if (!parsed) return undefined;

  const normalizedStep = Math.max(1, step);
  const totalMinutes = parsed.hour * 60 + parsed.minute;
  const remainder = totalMinutes % normalizedStep;

  const delta =
    direction === 'up'
      ? remainder === 0
        ? normalizedStep
        : normalizedStep - remainder
      : remainder === 0
        ? normalizedStep
        : remainder;

  const nextTotalMinutes =
    direction === 'up'
      ? (totalMinutes + delta) % MINUTES_PER_DAY
      : (totalMinutes - delta + MINUTES_PER_DAY) % MINUTES_PER_DAY;

  const nextHour = Math.floor(nextTotalMinutes / 60);
  const nextMinute = nextTotalMinutes % 60;

  return `${String(nextHour).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}`;
};

/** Convert a 24h hour (0-23) to 12h + period. */
export const to12Hour = (h24: number): { hour12: number; period: 'AM' | 'PM' } => {
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  let hour12 = h24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, period };
};

/** Convert a 12h hour + period back to 24h hour (0-23). */
export const to24Hour = (hour12: number, period: 'AM' | 'PM'): number => {
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
};
